import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

const INPUT_STYLE = {
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 6,
  border: '1px solid #CBD5E1',
  backgroundColor: '#FFFFFF',
  fontSize: 14,
  lineHeight: '20px',
  padding: '10px 12px',
  color: '#0F172A',
  outline: 'none',
};

export default function LocationAutoComplete({ style, placeholder, onSelectLocation }) {
  const inputRef = useRef(null);
  const placesLibrary = useMapsLibrary('places');
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const timerRef = useRef(null);
  const sessionRef = useRef(null);

  const updateMenuPos = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    const onWin = () => updateMenuPos();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, updateMenuPos]);

  useEffect(() => {
    if (!placesLibrary || !query.trim() || query.trim().length < 2) {
      setPredictions([]);
      return undefined;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!sessionRef.current && window.google?.maps?.places) {
        sessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      const service = new placesLibrary.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query.trim(),
          componentRestrictions: { country: 'id' },
          sessionToken: sessionRef.current || undefined,
        },
        (results, status) => {
          const ok = !window.google?.maps?.places
            || status === window.google.maps.places.PlacesServiceStatus.OK
            || status === 'OK';
          setPredictions(ok && Array.isArray(results) ? results : []);
          setOpen(true);
          updateMenuPos();
        },
      );
    }, 280);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [placesLibrary, query, updateMenuPos]);

  const selectPrediction = (prediction) => {
    const placeId = prediction?.place_id;
    const label = prediction?.description || '';
    setQuery(label);
    setPredictions([]);
    setOpen(false);
    if (!placeId || !placesLibrary) return;

    const attr = document.createElement('div');
    attr.style.display = 'none';
    document.body.appendChild(attr);
    const service = new placesLibrary.PlacesService(attr);
    service.getDetails(
      {
        placeId,
        fields: ['geometry', 'formatted_address', 'name'],
        sessionToken: sessionRef.current || undefined,
      },
      (place) => {
        attr.remove();
        sessionRef.current = null;
        const loc = place?.geometry?.location;
        if (!loc) return;
        onSelectLocation({
          latitude: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
          longitude: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
        });
      },
    );
  };

  const menu = open && predictions.length > 0 && menuPos && typeof document !== 'undefined'
    ? createPortal(
      <div
        style={{
          position: 'fixed',
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width,
          maxHeight: 240,
          overflowY: 'auto',
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
          zIndex: 2147483647,
        }}
      >
        {predictions.map((item) => (
          <button
            key={item.place_id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => selectPrediction(item)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              border: 0,
              borderBottom: '1px solid #F1F5F9',
              background: '#FFFFFF',
              fontSize: 13,
              lineHeight: '18px',
              color: '#0F172A',
              cursor: 'pointer',
            }}
          >
            {item.description}
          </button>
        ))}
      </div>,
      document.body,
    )
    : null;

  return (
    <div style={{ width: '100%', marginBottom: 12, position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={placeholder || 'Cari alamatmu'}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (predictions.length) setOpen(true);
          updateMenuPos();
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 180);
        }}
        style={{ ...INPUT_STYLE, ...style, marginBottom: 0 }}
      />
      {menu}
    </div>
  );
}
